import apiClient from '../client'
import type { User, PaginatedResponse } from '@/types'

export interface UserFilters {
  search?: string
  role?: 'admin' | 'sales_manager' | 'standard'
  is_active?: boolean
  page?: number
  page_size?: number
}

// Backend response type
interface BackendUsersResponse {
  users: User[]
  total: number
  page: number
  page_size: number
}

// Transform backend response to frontend PaginatedResponse format
const transformUsersResponse = (
  backendData: BackendUsersResponse,
  page: number,
  pageSize: number
): PaginatedResponse<User> => ({
  items: backendData.users || [],
  total: backendData.total || 0,
  page: page,
  page_size: pageSize,
  total_pages: Math.ceil((backendData.total || 0) / pageSize),
})

export interface CreateUserData {
  email: string
  name?: string
  role?: 'admin' | 'sales_manager' | 'standard'
  is_active?: boolean
}

export interface UpdateUserData {
  name?: string
  role?: 'admin' | 'sales_manager' | 'standard'
  is_active?: boolean
}

export interface UpdateRoleData {
  role: 'admin' | 'sales_manager' | 'standard'
}

export interface UpdateStatusData {
  is_active: boolean
}

export const usersApi = {
  getAll: async (filters: UserFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 20

    const response = await apiClient.get<BackendUsersResponse>('/api/users', { params: filters })

    return {
      ...response,
      data: transformUsersResponse(response.data, page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<User>(`/api/users/${id}`),

  getMe: () =>
    apiClient.get<User>('/api/users/me'),

  create: (data: CreateUserData) =>
    apiClient.post<User>('/api/users', data),

  // Combined update that calls both role and status endpoints if needed
  update: async (id: string, data: UpdateUserData) => {
    const results: { status: string; message: string }[] = []

    if (data.role !== undefined) {
      const roleResult = await apiClient.put<{ status: string; message: string }>(`/api/users/${id}/role`, { role: data.role })
      results.push(roleResult.data)
    }

    if (data.is_active !== undefined) {
      const statusResult = await apiClient.put<{ status: string; message: string }>(`/api/users/${id}/status`, { is_active: data.is_active })
      results.push(statusResult.data)
    }

    return { data: results.length > 0 ? results[results.length - 1] : { status: 'success', message: 'No changes' } }
  },

  updateRole: (id: string, data: UpdateRoleData) =>
    apiClient.put<{ status: string; message: string }>(`/api/users/${id}/role`, data),

  updateStatus: (id: string, data: UpdateStatusData) =>
    apiClient.put<{ status: string; message: string }>(`/api/users/${id}/status`, data),

  delete: (id: string) =>
    apiClient.delete<{ status: string; message: string }>(`/api/users/${id}`),

  getUserLeads: (id: string, page?: number, pageSize?: number, ownership?: 'owned' | 'assigned' | 'all') =>
    apiClient.get<{ leads: unknown[]; total: number }>(`/api/users/${id}/leads`, {
      params: { page, page_size: pageSize, ownership }
    }),
}
